// Vertical rule opening every column group of AgGrid.vue — on the group header, and on the header
// and cells of the group's first displayed column — so grouped blocks (Commandé / Validé / Retours,
// one block per store, P1 / P2…) read as separate instead of one run of look-alike columns. Every
// grid gets it as soon as a colDef has children; styles live in src/styles/_agGrid.scss.
//
// The first column of a group is decided at render time rather than baked into the first child of
// the colDef: it changes whenever the user hides or moves a column (tool panel, drag). AgGrid.vue
// re-runs the header classes and cellClassRules when the displayed columns change.

export const GROUP_START_CLASS = { header: 'ag-grid-wrapper__header--group-start', cell: 'ag-grid-wrapper__cell--group-start' };

/**
 * True when `column` is the first displayed leaf of one of its column groups, at any depth. Padding
 * groups — the ones AG Grid inserts above ungrouped columns to even out the header depth — are not
 * real groups and never count.
 *
 * @param {object} column AG Grid Column
 * @returns {boolean}
 */
export function isColumnGroupStart(column) {
	let group = column?.getParent?.();
	while (group) {
		if (!group.isPadding?.() && group.getDisplayedLeafColumns?.()[0] === column) return true;
		group = group.getParent?.();
	}
	return false;
}

function toClassList(rule, params) {
	const value = typeof rule === 'function' ? rule(params) : rule;
	if (!value) return [];
	return Array.isArray(value) ? value : [value];
}

// headerClass accepts a string, an array or a function: the caller's own value is kept whatever
// its form, and the divider class is appended to it.
function appendHeaderClass(headerClass, extra) {
	return (params) => [...toClassList(headerClass, params), ...toClassList(extra, params)];
}

const groupStartHeaderClass = (params) => (isColumnGroupStart(params?.column) ? GROUP_START_CLASS.header : null);
const isGroupStartCell = (params) => isColumnGroupStart(params?.column);

/**
 * Adds the divider classes to a colDef tree. A group's own header always opens the group; a leaf
 * inside a group gets a rule that is only true while it is that group's first displayed column.
 * Leaves outside any group are returned untouched.
 */
export function withGroupDividers(colDef, inGroup = false) {
	if (!colDef || typeof colDef !== 'object') return colDef;

	if (Array.isArray(colDef.children)) {
		return {
			...colDef,
			headerClass: appendHeaderClass(colDef.headerClass, GROUP_START_CLASS.header),
			children: colDef.children.map((child) => withGroupDividers(child, true)),
		};
	}

	if (!inGroup) return colDef;

	return {
		...colDef,
		headerClass: appendHeaderClass(colDef.headerClass, groupStartHeaderClass),
		cellClassRules: { ...(colDef.cellClassRules || {}), [GROUP_START_CLASS.cell]: isGroupStartCell },
	};
}
